// Web implementation of push notification helpers.

import { onMessage } from 'firebase/messaging';
import { getFirebaseMessaging, useFirebaseUser } from 'internal/firebase';
import type { BrowserPushPermissionState } from 'open-modules/zdf/component/notifications/push-notification';
import { getPublicWebPushKey } from 'open-modules/zdf/component/notifications/push-notification';
import { useEffect } from 'react';
import { callServerApiAsync } from 'system/servercall';
import { usePersonaKey } from 'util/datastore';
import { getDeploymentConfig } from 'util/util';

import {
    PermissionPopupClient,
    PermissionPopupClientIframe,
    requestBrowserPushNotificationClientHandler,
    requestBrowserPushNotificationPopupWindowHandler,
} from './browserPushNotificationPopupClient.web';
import { PermissionPopupHost } from './browserPushNotificationPopupHost.web';
import {
    arePushNotificationsHandledOnHostSite,
    arePushNotificationsSupported,
    fetchBrowserPushNotificationTokenClientAsync,
    getUserHasHandledPushNotificationDialog,
    isIframe,
    registerBrowserPushNotificationServiceWorkerAsync,
    updateServiceWorkerRegistrationAndFetchTokenAsync,
} from './pushNotificationUtils.web';

// Setup push Notification in case the service worker is registered on the host site outside of psi client
export function useMaybeSetupPushNotificationsHost(siloKey) {
    const firebaseConfig = getDeploymentConfig().firebaseConfig;
    const pubPushKey = getPublicWebPushKey();
    const user = useFirebaseUser();

    const doNotSetup = !isIframe() || !arePushNotificationsHandledOnHostSite() || !arePushNotificationsSupported() || !user;

    // Update the message handler for saving the retrieved push token
    useEffect(() => {
        const handleMessage = async (event) => {
            if (!user) return;
            if (event.data.type === 'psi-push-notification-setup-done') {
                callServerApiAsync({
                    component: 'pushNotifications',
                    funcname: 'savePushToken',
                    params: {
                        siloKey,
                        token: event.data.token,
                    },
                });
            }
        };
        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [user]);

    useEffect(() => {
        if (doNotSetup) return;
        console.log('Setting up push notifications on the host website');
        window.parent.postMessage({ type: 'psi-setup-firebase-messaging', config: firebaseConfig, vapidKey: pubPushKey }, '*');
    }, [user]);
}

export function PushPermissionPopup({ onAllow, onNotAllow }) {
    const pushNotificationAvailable = arePushNotificationsSupported();
    const handleNotificationsonHostSite = arePushNotificationsHandledOnHostSite();
    const userKey = usePersonaKey();

    //TODO: Add the possibility to register service worker on the client site later

    if (!pushNotificationAvailable || !userKey) {
        return null;
    } else if (handleNotificationsonHostSite) {
        return <PermissionPopupHost onAllow={onAllow} onNotAllow={onNotAllow} />;
    } else if (isIframe()) {
        return <PermissionPopupClientIframe onAllow={onAllow} onNotAllow={onNotAllow} />;
    } else {
        return <PermissionPopupClient onAllow={onAllow} onNotAllow={onNotAllow} />;
    }
}

export function getBrowserNotificationPermission(): BrowserPushPermissionState {
    try {
        console.log(Notification.permission);
        return Notification.permission;
    } catch (e) {
        console.log(e);
        return 'default';
    }
}

export function useMaybeSetupHandlingOfBrowserPushNotifications() {
    useEffect(() => {
        const messaging = getFirebaseMessaging();

        if (messaging && !arePushNotificationsHandledOnHostSite() && arePushNotificationsSupported()) {
            const unsubscribe = onMessage(messaging, (payload) => {
                const notificationTitle = payload.data?.title ?? '';
                const body = payload.data?.body;
                const url = payload.data?.link;
                const notificationOptions = {
                    body: body,
                    link: url,
                    icon: payload.data?.icon,
                };
                const notification = new Notification(notificationTitle, {
                    tag: notificationTitle,
                    body: notificationOptions.body,
                    icon: notificationOptions.icon,
                });
                notification.onclick = function () {
                    window.open(notificationOptions.link, '_blank');
                };
            });
            console.log('Setup incoming notifications');

            return () => {
                unsubscribe();
            };
        }
    }, []);
}

export function useMaybeSetupPushNotificationsClient(siloKey) {
    const user = useFirebaseUser();
    const hasPermission = getBrowserNotificationPermission() === 'granted';
    const browserHasDeclined = getUserHasHandledPushNotificationDialog();

    const doNotSetup =
        arePushNotificationsHandledOnHostSite() || !arePushNotificationsSupported() || !user || !hasPermission || browserHasDeclined;
    const updatePushNotificationsAsync = async () => {
        const token = await updateServiceWorkerRegistrationAndFetchTokenAsync();
        callServerApiAsync({
            component: 'pushNotifications',
            funcname: 'savePushToken',
            params: {
                siloKey: siloKey,
                token: token,
            },
        });
        console.log('PN Setup done');
    };

    useEffect(() => {
        if (doNotSetup) return;
        updatePushNotificationsAsync();
    }, [user]);
}

export function useMaybeSetupPushNotifications(siloKey) {
    useMaybeSetupPushNotificationsHost(siloKey);
    useMaybeSetupPushNotificationsClient(siloKey);
    useMaybeSetupHandlingOfBrowserPushNotifications();
}

export function requestBrowserPushNotificationHandler({
    datastore,
    onFinish = (permission?: BrowserPushPermissionState) => {},
    onStart = () => {},
    onCancel = () => {},
    onError = () => {},
}) {
    if (arePushNotificationsHandledOnHostSite()) {
        return {
            requestPermission: () => {},
        };
    }

    if (isIframe()) {
        return requestBrowserPushNotificationPopupWindowHandler({
            onCancel,
            onFinish,
            onStart,
        });
    } else {
        return requestBrowserPushNotificationClientHandler({
            datastore,
            onFinish,
            onStart,
            onCancel,
            onError,
        });
    }
}

export {
    arePushNotificationsHandledOnHostSite,
    arePushNotificationsSupported,
    fetchBrowserPushNotificationTokenClientAsync,
    getUserHasHandledPushNotificationDialog,
    isIframe,
    registerBrowserPushNotificationServiceWorkerAsync,
    updateServiceWorkerRegistrationAndFetchTokenAsync,
} from './pushNotificationUtils.web';
